import { Search, Filter, MapPin, Plus, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { findSuggestions, type SuggestionCandidate } from "@/lib/fuzzySuggest";
import type { Shelter } from "@/types/shelter";
import { useMemo } from "react";

interface EmptyStateProps {
  type?: "no-results" | "no-data";
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
  searchQuery?: string;
  city?: string;
  allShelters?: Shelter[];
  onSuggestionClick?: (query: string) => void;
  /**
   * Jawny strażnik: sugestie pokazujemy TYLKO gdy rodzic potwierdza, że
   * - lista wyników jest faktycznie pusta (zero schronisk),
   * - po uwzględnieniu wszystkich filtrów POZA searchQuery istnieją kandydaci,
   * - status wyszukiwania jest "settled" (debounce zakończony, dane wczytane).
   * Domyślnie true dla kompatybilności wstecznej.
   */
  suggestionsEnabled?: boolean;
}

export const EmptyState = ({
  type = "no-results",
  onClearFilters,
  hasActiveFilters = false,
  searchQuery = "",
  city = "",
  allShelters = [],
  onSuggestionClick,
  suggestionsEnabled = true,
}: EmptyStateProps) => {
  const trimmed = searchQuery.trim();

  const suggestions = useMemo(() => {
    if (!trimmed || allShelters.length === 0) return [];
    const candidates: SuggestionCandidate[] = [];
    for (const s of allShelters) {
      candidates.push({ label: s.city, kind: "miasto" });
      if (s.region) candidates.push({ label: s.region, kind: "województwo" });
      candidates.push({ label: s.name, kind: "schronisko" });
    }
    return findSuggestions(trimmed, candidates, 3);
  }, [trimmed, allShelters]);

  // Build prefill URL for /zglos
  const prefillParams = new URLSearchParams();
  if (trimmed) prefillParams.set("name", trimmed);
  if (city) prefillParams.set("city", city);
  const submitUrl = `/zglos${prefillParams.toString() ? `?${prefillParams.toString()}` : ""}`;

  return (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center animate-fade-in">
      {/* Illustration */}
      <div className="relative mb-5">
        <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
          {type === "no-results" ? (
            <Search className="w-9 h-9 text-muted-foreground/50" />
          ) : (
            <MapPin className="w-9 h-9 text-muted-foreground/50" />
          )}
        </div>
        {type === "no-results" && (
          <div className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
            <Filter className="w-4 h-4 text-primary/60" />
          </div>
        )}
      </div>

      {/* Text */}
      <h3 className="font-display font-semibold text-lg text-foreground mb-2">
        {type === "no-results" ? "Brak wyników" : "Brak schronisk"}
      </h3>
      <p className="text-sm text-muted-foreground max-w-[300px] mb-4">
        {type === "no-results"
          ? trimmed
            ? <>Nie znaleziono nic dla „<strong className="text-foreground">{trimmed}</strong>".</>
            : "Nie znaleziono schronisk pasujących do wybranych filtrów."
          : "Nie udało się załadować listy schronisk. Odśwież stronę, aby spróbować ponownie."}
      </p>

      {/* Did you mean suggestions */}
      {type === "no-results" && suggestionsEnabled && suggestions.length > 0 && onSuggestionClick && (
        <div className="w-full max-w-[320px] mb-4 p-3 bg-accent/40 border border-border rounded-xl">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
            <Lightbulb className="w-3.5 h-3.5 text-primary" />
            <span>Czy chodziło Ci o:</span>
          </div>
          <div className="flex flex-wrap gap-1.5 justify-center">
            {suggestions.map((s) => (
              <button
                key={`${s.kind}-${s.label}`}
                type="button"
                onClick={() => onSuggestionClick(s.label)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background border border-border text-xs hover:bg-primary/10 hover:border-primary/30 transition-colors"
              >
                <span className="font-medium text-foreground">{s.label}</span>
                <span className="text-muted-foreground">· {s.kind}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-2 w-full max-w-[280px]">
        {type === "no-results" && hasActiveFilters && onClearFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="gap-2"
          >
            <Filter className="w-4 h-4" />
            Wyczyść filtry
          </Button>
        )}

        {type === "no-results" && (
          <Button asChild size="sm" className="gap-2">
            <Link to={submitUrl}>
              <Plus className="w-4 h-4" />
              Zgłoś brakujące schronisko
            </Link>
          </Button>
        )}
      </div>

      {type === "no-results" && (
        <p className="text-[11px] text-muted-foreground mt-3 max-w-[280px]">
          Pomożemy je dodać do mapy 🐾
        </p>
      )}
    </div>
  );
};
